/**
 * WorklistCaptureForm — one-minute capture for a Fix or Addition against
 * the Rulebook. Title required; everything else optional.
 *
 * DESIGN PASS 4: converted onto the token system with the Rule Book page.
 * Styles live in styles/pages/rulebook.css, scoped to .km-rulebook.
 * This file is OFF the design-token allowlist.
 */
import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Upload, AlertOctagon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorklistCaptureForm({
  homeFeatureId, // string | null (null = New / Unassigned)
  allFeatures,   // [{id, name}]
  onCreated,     // (item) => void
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('fix');
  const [blocking, setBlocking] = useState(false);
  const [proposedNewFeature, setProposedNewFeature] = useState(false);
  const [feature, setFeature] = useState(homeFeatureId || '');
  const [alsoAffects, setAlsoAffects] = useState([]); // [{feature_id, note}]
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const canSubmit = title.trim().length > 0 && !submitting;

  const toggleAffected = (fid) => {
    setAlsoAffects(prev => {
      const found = prev.find(x => x.feature_id === fid);
      if (found) return prev.filter(x => x.feature_id !== fid);
      return [...prev, { feature_id: fid, note: '' }];
    });
  };

  const setAffectedNote = (fid, note) => {
    setAlsoAffects(prev => prev.map(x =>
      x.feature_id === fid ? { ...x, note } : x
    ));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        type,
        blocking,
        feature_id: feature || null,
        also_affects: alsoAffects.filter(a => a.feature_id && a.feature_id !== feature),
        proposed_new_feature: !feature && proposedNewFeature,
      };
      const { data: item } = await axios.post(`${API}/rulebook/worklist`, body);

      if (screenshotFile) {
        const fd = new FormData();
        fd.append('file', screenshotFile);
        try {
          const { data: sc } = await axios.post(
            `${API}/rulebook/worklist/${item.id}/screenshot`, fd,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
          item.screenshot_url = sc.screenshot_url;
        } catch (e) {
          toast.error('Screenshot upload failed — item still saved.');
        }
      }

      toast.success('Captured.');
      setTitle(''); setDescription(''); setBlocking(false);
      setAlsoAffects([]); setScreenshotFile(null); setProposedNewFeature(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onCreated?.(item);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="km-capture space-y-3" data-testid="worklist-capture-form">
      <div className="km-eyebrow">
        <Plus className="w-3 h-3" /> Add fix or addition
      </div>

      <input
        className="km-input"
        placeholder="Title (required) — what happened vs what should happen"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        data-testid="worklist-form-title"
      />
      <textarea
        className="km-textarea"
        placeholder="Optional description — details, steps, expected vs actual"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        data-testid="worklist-form-description"
      />

      <div className="flex flex-wrap items-center gap-4">
        {/* Type */}
        <div className="flex items-center gap-1">
          <span className="km-meta">Type:</span>
          {['fix', 'addition'].map(t => (
            <button
              key={t}
              onClick={() => setType(t)}
              className="km-seg"
              data-on={type === t}
              data-testid={`worklist-form-type-${t}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Blocking */}
        <label className="km-checkline" data-on={blocking}>
          <input
            type="checkbox"
            checked={blocking}
            onChange={(e) => setBlocking(e.target.checked)}
            data-testid="worklist-form-blocking"
          />
          <span className="flex items-center gap-1">
            {blocking && <AlertOctagon className="w-3 h-3" />}
            Blocking live project
          </span>
        </label>

        {/* Screenshot */}
        <label className="km-checkline">
          <Upload className="w-3 h-3" />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => setScreenshotFile(e.target.files?.[0] || null)}
            className="km-file"
            data-testid="worklist-form-screenshot-input"
          />
          {screenshotFile && (
            <span className="km-meta truncate max-w-[10rem]">{screenshotFile.name}</span>
          )}
        </label>
      </div>

      {/* Home feature (only shown from aggregate/unassigned entry) */}
      {!homeFeatureId && (
        <div>
          <label className="km-field-label">Home feature (leave blank for New / Unassigned)</label>
          <select
            value={feature}
            onChange={(e) => setFeature(e.target.value)}
            className="km-select"
            data-testid="worklist-form-home-feature"
          >
            <option value="">— New / Unassigned —</option>
            {allFeatures.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          {!feature && (
            <label className="km-checkline mt-2">
              <input
                type="checkbox"
                checked={proposedNewFeature}
                onChange={(e) => setProposedNewFeature(e.target.checked)}
                data-testid="worklist-form-proposed-new-feature"
              />
              Proposed new Rulebook feature
            </label>
          )}
        </div>
      )}

      {/* Also affects */}
      <details>
        <summary className="km-summary">Also affects other features (optional)</summary>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1">
          {allFeatures.filter(f => f.id !== (feature || homeFeatureId)).map(f => {
            const affected = alsoAffects.find(x => x.feature_id === f.id);
            return (
              <div key={f.id}>
                <button
                  onClick={() => toggleAffected(f.id)}
                  className="km-affect"
                  data-on={!!affected}
                  data-testid={`worklist-form-affects-${f.id}`}
                >
                  {f.name}
                </button>
                {affected && (
                  <input
                    className="km-affect-note"
                    placeholder="how it affects this area"
                    value={affected.note}
                    onChange={(e) => setAffectedNote(f.id, e.target.value)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </details>

      <div className="flex justify-end">
        <button
          className="km-btn km-btn--primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="worklist-form-submit"
        >
          {submitting ? 'Saving…' : 'Capture'}
        </button>
      </div>
    </div>
  );
}
